// Claim form page — /warranty/new (authenticated)
// Scan serial → preview → pick/quick-create customer → describe issue → submit
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { apiPost, apiGet } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { ShieldCheck, ShieldOff, Search } from "lucide-react";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:8080";

interface PublicSerialInfo {
  productName: string; variantName: string; sku: string; serialNo: string;
  purchasedAt?: string; warrantyStart?: string; warrantyEnd?: string;
  daysRemaining: number; isValid: boolean; status: string;
}

interface Customer {
  id: string; phone: string; name: string;
}

interface CreateClaimBody {
  serialNo: string;
  customerId?: string;
  customerQuickCreate?: { phone: string; name: string };
  issueDesc: string;
}

function fmt(d?: string) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("vi-VN");
}

export function ClaimFormPage() {
  const navigate = useNavigate();

  // Serial step
  const [serialInput, setSerialInput] = useState("");
  const [serialInfo, setSerialInfo]   = useState<PublicSerialInfo | null>(null);
  const [serialErr, setSerialErr]     = useState<string | null>(null);
  const [serialLoading, setSerialLoading] = useState(false);

  // Customer step
  const [phoneSearch, setPhoneSearch]     = useState("");
  const [foundCustomer, setFoundCustomer] = useState<Customer | null>(null);
  const [customerMode, setCustomerMode]   = useState<"none" | "found" | "quick">("none");
  const [quickName, setQuickName]         = useState("");
  const [customerLoading, setCustomerLoading] = useState(false);

  // Issue
  const [issueDesc, setIssueDesc] = useState("");
  const [submitErr, setSubmitErr] = useState<string | null>(null);

  // --- Serial lookup (public endpoint, no auth header) ---
  async function lookupSerial() {
    const val = serialInput.trim();
    if (!val) return;
    setSerialLoading(true);
    setSerialErr(null);
    setSerialInfo(null);
    try {
      const res = await axios.get<{ data: PublicSerialInfo }>(
        `${API_BASE}/public/warranty/${encodeURIComponent(val)}`
      );
      setSerialInfo(res.data.data);
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { error?: { message?: string } } } };
      if (e.response?.status === 404) setSerialErr("Không tìm thấy serial này.");
      else if (e.response?.status === 429) setSerialErr("Tra cứu quá nhanh, thử lại sau.");
      else setSerialErr("Lỗi khi tra cứu serial.");
    } finally {
      setSerialLoading(false);
    }
  }

  // --- Customer phone lookup (authenticated) ---
  async function searchCustomer() {
    const phone = phoneSearch.trim();
    if (!phone) return;
    setCustomerLoading(true);
    try {
      const res = await apiGet<Customer[]>(`/v1/customers/search?q=${encodeURIComponent(phone)}&limit=1`);
      const list = Array.isArray(res) ? res : (res as any).data ?? [];
      if (list.length > 0) {
        setFoundCustomer(list[0]);
        setCustomerMode("found");
      } else {
        setFoundCustomer(null);
        setCustomerMode("quick");
      }
    } catch {
      setCustomerMode("quick");
    } finally {
      setCustomerLoading(false);
    }
  }

  // --- Submit ---
  const submit = useMutation({
    mutationFn: (body: CreateClaimBody) =>
      apiPost<{ id: string }>("/v1/warranty/claims", body),
    onSuccess: (res) => {
      const claim = res as unknown as { id: string };
      navigate({ to: `/warranty/${claim.id}` as any });
    },
    onError: (err: unknown) => {
      setSubmitErr(translateApiError(err));
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!serialInfo) return;
    if (!issueDesc.trim()) { setSubmitErr("Vui lòng mô tả lỗi."); return; }

    const body: CreateClaimBody = {
      serialNo: serialInfo.serialNo,
      issueDesc: issueDesc.trim(),
    };

    if (customerMode === "found" && foundCustomer) {
      body.customerId = foundCustomer.id;
    } else if (customerMode === "quick" && phoneSearch.trim()) {
      body.customerQuickCreate = { phone: phoneSearch.trim(), name: quickName.trim() || phoneSearch.trim() };
    }

    setSubmitErr(null);
    submit.mutate(body);
  }

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <h1 className="text-2xl font-bold">Tiếp nhận bảo hành</h1>

      {/* ── Step 1: Serial ── */}
      <section className="rounded-xl border bg-card p-5 space-y-3">
        <h2 className="font-semibold">1. Tra cứu serial</h2>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Nhập hoặc quét serial..."
            className="flex-1 rounded-md border bg-background px-3 py-2 text-sm"
            value={serialInput}
            onChange={(e) => setSerialInput(e.target.value)}
            onBlur={lookupSerial}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), lookupSerial())}
          />
          <button
            type="button"
            onClick={lookupSerial}
            disabled={serialLoading}
            className="flex items-center gap-1 rounded-md border px-3 py-2 text-sm disabled:opacity-50"
          >
            <Search size={15} /> Tìm
          </button>
        </div>
        {serialErr && <p className="text-sm text-destructive">{serialErr}</p>}
        {serialInfo && (
          <div className={`rounded-lg p-3 text-sm ${serialInfo.isValid ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
            <div className="flex items-center gap-2 font-semibold mb-1">
              {serialInfo.isValid ? <ShieldCheck size={16} className="text-green-600" /> : <ShieldOff size={16} className="text-red-600" />}
              {serialInfo.isValid ? "Còn bảo hành" : "Hết bảo hành"} — sẽ tạo phiếu {serialInfo.isValid ? "bảo hành" : "sửa có phí"}
            </div>
            <p><span className="text-muted-foreground">Sản phẩm:</span> {serialInfo.productName} — {serialInfo.variantName}</p>
            <p><span className="text-muted-foreground">Ngày mua:</span> {fmt(serialInfo.purchasedAt)}</p>
            <p><span className="text-muted-foreground">Hết hạn BH:</span> {fmt(serialInfo.warrantyEnd)}</p>
            {serialInfo.isValid && <p className="text-green-700 font-medium">Còn {serialInfo.daysRemaining} ngày</p>}
          </div>
        )}
      </section>

      {/* ── Step 2: Customer (only if serial found) ── */}
      {serialInfo && (
        <section className="rounded-xl border bg-card p-5 space-y-3">
          <h2 className="font-semibold">2. Khách hàng</h2>
          <div className="flex gap-2">
            <input
              type="tel"
              placeholder="Số điện thoại khách..."
              className="flex-1 rounded-md border bg-background px-3 py-2 text-sm"
              value={phoneSearch}
              onChange={(e) => { setPhoneSearch(e.target.value); setCustomerMode("none"); setFoundCustomer(null); }}
              onBlur={searchCustomer}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), searchCustomer())}
            />
            <button type="button" onClick={searchCustomer} disabled={customerLoading}
              className="rounded-md border px-3 py-2 text-sm disabled:opacity-50">
              {customerLoading ? "..." : "Tìm KH"}
            </button>
          </div>

          {customerMode === "found" && foundCustomer && (
            <div className="rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-sm">
              Đã tìm thấy: <strong>{foundCustomer.name}</strong> — {foundCustomer.phone}
            </div>
          )}

          {customerMode === "quick" && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Không tìm thấy, tạo khách hàng mới:</p>
              <input
                type="text"
                placeholder="Tên khách hàng (tuỳ chọn)"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={quickName}
                onChange={(e) => setQuickName(e.target.value)}
              />
            </div>
          )}

          {customerMode === "none" && (
            <p className="text-xs text-muted-foreground">Bỏ qua nếu không cần lưu thông tin khách.</p>
          )}
        </section>
      )}

      {/* ── Step 3: Issue description ── */}
      {serialInfo && (
        <section className="rounded-xl border bg-card p-5 space-y-3">
          <h2 className="font-semibold">3. Mô tả lỗi</h2>
          <textarea
            rows={4}
            placeholder="Mô tả tình trạng lỗi của sản phẩm..."
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            value={issueDesc}
            onChange={(e) => setIssueDesc(e.target.value)}
          />
        </section>
      )}

      {/* ── Submit ── */}
      {serialInfo && (
        <div className="space-y-2">
          {submitErr && <p className="text-sm text-destructive">{submitErr}</p>}
          <button
            onClick={handleSubmit}
            disabled={submit.isPending || !issueDesc.trim()}
            className="w-full rounded-md bg-primary py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            {submit.isPending ? "Đang tạo phiếu..." : "Tạo phiếu bảo hành"}
          </button>
        </div>
      )}
    </div>
  );
}
